import { DestroyRef } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApplicationStatus, HouseholdStudent, StudentEsa, StudentOs } from '@k12/models';
import { ConfirmDialogService } from '@k12/ui-primitives';
import { Observable, of } from 'rxjs';
import { IneligibilityReason } from '../awards/models/ineligibilityReason.model';

interface EditableStudentData {
  detail?: HouseholdStudent | null;
  inEligibilityReasons?: IneligibilityReason[] | null;
}

function reasonName(reasons: IneligibilityReason[] | null | undefined, id: number | null): string | null {
  return reasons?.find((r) => r.id === id)?.name ?? null;
}

export function withSavedOs<T extends EditableStudentData>(studentData: T, os: StudentOs): T & { os: StudentOs } {
  return {
    ...studentData,
    os,
    detail: studentData?.detail && {
      ...studentData.detail,
      osApplicationStatus: os.osApplicationStatus,
      osIneligibilityReason: reasonName(studentData.inEligibilityReasons, os.osIneligibilityReasonId)
    }
  };
}

export function withSavedEsa<T extends EditableStudentData>(studentData: T, esa: StudentEsa): T & { esa: StudentEsa } {
  return {
    ...studentData,
    esa,
    detail: studentData?.detail && {
      ...studentData.detail,
      esaApplicationStatus: esa.esaApplicationStatus,
      esaIneligibilityReason: reasonName(studentData.inEligibilityReasons, esa.esaIneligibilityReasonId)
    }
  };
}

export function requireReasonWhenIneligible(
  form: FormGroup,
  statusKey: string,
  reasonKey: string,
  destroyRef: DestroyRef
): void {
  const status = form.get(statusKey);
  const reason = form.get(reasonKey);
  if (!status || !reason) return;

  const apply = (value: unknown) => {
    reason.setValidators(value === ApplicationStatus.Ineligible ? [Validators.required] : []);
    reason.updateValueAndValidity({ emitEvent: false });
  };

  apply(status.value);
  status.valueChanges.pipe(takeUntilDestroyed(destroyRef)).subscribe(apply);
}

export function omitUnchangedEligibility<T extends object>(
  payload: T,
  statusKey: keyof T,
  reasonKey: keyof T,
  original: Partial<T> | null | undefined
): T {
  const unchanged =
    !!original &&
    (payload[statusKey] ?? null) === (original[statusKey] ?? null) &&
    (payload[reasonKey] ?? null) === (original[reasonKey] ?? null);
  return unchanged ? { ...payload, [statusKey]: null, [reasonKey]: null } : payload;
}

export function confirmIneligibleTaskCancellation(
  confirmDialog: ConfirmDialogService,
  previousStatus: string | null | undefined,
  newStatus: string | null | undefined,
  programName: string
): Observable<boolean> {
  if (newStatus !== ApplicationStatus.Ineligible || previousStatus === ApplicationStatus.Ineligible) {
    return of(true);
  }

  return confirmDialog.confirm({
    title: 'Mark application Ineligible?',
    message: `Saving will cancel all of this student's pending ${programName} tasks. This cannot be undone.`,
    confirmButtonText: 'Mark Ineligible'
  });
}
